import { router, usePathname } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { queryClient } from '@fhs/client';
import { loyaltyIncentivesQueryKeys } from '@fhs/loyalty/src/queries/loyalty.queries';
import { AlertDialog, Button, Text, tokens } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { usePaymentFlowStore } from '../payment';

export function CartCommitLoading() {
  const { showBackModal, isCommitting, handleStayOnPage, handleLeave } = useCommitNavigationGuard();

  // No back browser button on native
  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <AlertDialog
      visible={showBackModal && isCommitting}
      onClose={handleStayOnPage}
      hideCloseIcon
      accessibilityLabel="Order Placed!"
      headingContent="Order Placed!"
      bodyContent={
        <View style={styles.bodyContent}>
          <Text.Paragraph size="md">
            We're sending your order to the store. If you leave, your order will still be processed
            and you will be taken to the order confirmation page.
          </Text.Paragraph>
        </View>
      }
      actions={
        <>
          <Button size="xl" type="solid" onPress={handleStayOnPage}>
            <Button.Text>Stay on Page</Button.Text>
          </Button>
          <Button size="xl" type="outline" onPress={handleLeave}>
            <Button.Text>Leave Anyway</Button.Text>
          </Button>
        </>
      }
    />
  );
}

const styles = StyleSheet.create({
  bodyContent: {
    gap: 16,
    minWidth: 300,
  },
  urlContainer: {
    backgroundColor: tokens.colors.$houseLight,
    padding: 12,
    borderRadius: 8,
    gap: 4,
  },
  urlLabel: {
    color: tokens.colors.$blackOpacity55,
  },
  urlText: {
    color: tokens.colors.$black,
  },
});

function useCommitNavigationGuard() {
  const currentState = usePaymentFlowStore(state => state.currentState);
  const rbiOrderId = usePaymentFlowStore(state => state.rbiOrderId);
  const resetPaymentFlowStore = usePaymentFlowStore(state => state.resetPaymentFlowStore);
  const setCurrentState = usePaymentFlowStore(state => state.setCurrentState);
  const [showBackModal, setShowBackModal] = useState(false);
  const pathname = usePathname();

  const { emptyCart, setFireOrderIn } = useOrderContext();

  const isCommitting =
    currentState === 'committing' ||
    currentState === 'confirming' ||
    currentState === 'insertRequested';

  useEffect(() => {
    if (!isCommitting) {
      setShowBackModal(false);
    }
  }, [isCommitting]);

  // Track previous state so success/error side effects only fire on the actual
  // transition into that state. Without this guard, unstable refs in deps
  // (emptyCart, setFireOrderIn from useOrderContext) can re-fire the effect
  // on unrelated re-renders and force-redirect the user from any page.
  const prevStateRef = useRef(currentState);

  useEffect(() => {
    const prevState = prevStateRef.current;
    prevStateRef.current = currentState;

    if (prevState === currentState) {
      return;
    }

    if (currentState === 'success') {
      // Close modal if it's open before navigating and redirecting to order confirmation - order has been placed
      setShowBackModal(false);
      emptyCart();
      setFireOrderIn(0);
      // Remove loyalty incentives cache so redeemed config offers are not shown
      // on the offers page after a successful order without needing a page refresh.
      // removeQueries is used instead of invalidateQueries because the offers query
      // has refetchOnMount:false — invalidation alone would not trigger a refetch on mount.
      queryClient.removeQueries({ queryKey: loyaltyIncentivesQueryKeys.all });
      router.dismissTo({
        pathname: `${routes.orderConfirmation}/${rbiOrderId}`,
        params: { showToast: 'true' },
      });
      resetPaymentFlowStore();
    }
    if (currentState === 'error') {
      setShowBackModal(false);
      const onCartPayment =
        pathname === routes.cartPayment || pathname.startsWith(`${routes.cartPayment}/`);
      if (!onCartPayment) {
        router.dismissTo({
          pathname: routes.cartPayment,
        });
        resetPaymentFlowStore();
      }
    }
  }, [emptyCart, setFireOrderIn, resetPaymentFlowStore, currentState, rbiOrderId, pathname]);

  // Handle browser back button and beforeunload on web
  useEffect(() => {
    if (Platform.OS !== 'web' || !isCommitting) {
      return;
    }

    // Track if modal is currently showing to handle multiple back presses
    let isModalShowing = false;

    // BlueConic/BeApp wrap pushState and throw when offline — skip while disconnected.
    const pushCommitState = () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
      }
      try {
        window.history.pushState({ orderCommitting: true }, '');
      } catch {
        // ignore — back-guard is best-effort during offline commit
      }
    };

    pushCommitState();

    const handleOnline = () => {
      pushCommitState();
    };

    const handlePopState = () => {
      if (isModalShowing) {
        setShowBackModal(false);
        isModalShowing = false;
        pushCommitState();
      } else {
        pushCommitState();
        setShowBackModal(true);
        isModalShowing = true;
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
      return '';
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('online', handleOnline);
    };
  }, [isCommitting]);

  const handleStayOnPage = useCallback(() => {
    setShowBackModal(false);
  }, []);

  const handleLeave = useCallback(() => {
    setShowBackModal(false);
    // Set state to idle so pointer events are restored, but keep rbiOrderId
    // so the user can still see the order confirmation URL
    setCurrentState('idle');
    try {
      window.history.go(-2); // Go back past our the window state
    } catch {
    }
  }, [setCurrentState]);

  return {
    showBackModal,
    isCommitting,
    handleStayOnPage,
    handleLeave,
  };
}
